import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { EventCardSkeleton } from "@/components/EventCardSkeleton";
import { DiscoveryTeaserHome } from "@/components/discovery/DiscoveryTeaserHome";
import { LandingPageView } from "@/components/landing/LandingPageView";
import {
  ensureDiscoveryNavigationPending,
  useDiscoveryIndexRouteLifecycle,
} from "@/lib/discoveryNavLoading";
import { useAuth } from "@/lib/auth";
import { isLiveLaunch } from "@/lib/launchMode";
import { getLandingPage } from "@/lib/marketing.functions";
import { isSanityPreviewRequest } from "@/lib/sanity";
import { DEFAULT_LANDING_PAGE } from "@/lib/marketingDefaults";
import { supabase } from "@/lib/supabase";
import "@/styles/marketing.css";

const GUEST_KEY = "pulse.guest";

/** Client-only: skip Sanity when a real (non-guest) session will render Discovery. */
async function shouldSkipMarketingLanding(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(GUEST_KEY) === "1") return false;
    const { data } = await supabase.auth.getSession();
    return Boolean(data.session?.user);
  } catch {
    return false;
  }
}

const HomePage = lazy(() => import("@/pages/HomePage"));

function HomeFeedFallback() {
  useEffect(() => {
    ensureDiscoveryNavigationPending();
  }, []);

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-gradient-glow" />
      <header className="relative px-5 pt-14 pb-4">
        <div className="h-4 w-24 animate-pulse rounded bg-surface" />
        <div className="mt-3 h-9 w-40 animate-pulse rounded bg-surface" />
      </header>
      <div className="mt-6 grid grid-cols-1 gap-4 px-5 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <EventCardSkeleton key={`home-skeleton-${index}`} />
        ))}
      </div>
    </div>
  );
}

function DiscoveryIndex() {
  useDiscoveryIndexRouteLifecycle();
  return (
    <Suspense fallback={<HomeFeedFallback />}>
      <HomePage />
    </Suspense>
  );
}

function IndexRoute() {
  const { user, hydrated, isGuest } = useAuth();
  const { landing } = Route.useLoaderData();
  const live = isLiveLaunch();

  // Session present (real user) → Discovery; guests / logged-out → teaser (live) or waitlist (prelaunch).
  if (hydrated && user && !isGuest) {
    return <DiscoveryIndex />;
  }

  if (!hydrated) {
    return (
      <div className="marketing-root flex min-h-screen items-center justify-center">
        <p className="mono" style={{ color: "var(--muted)" }}>
          …
        </p>
      </div>
    );
  }

  if (live) {
    return <DiscoveryTeaserHome />;
  }

  return <LandingPageView data={landing} />;
}

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>) => search,
  loaderDeps: ({ search }) => ({ search }),
  /** Always revalidate for guests — marketing copy changes often in Studio. */
  staleTime: 0,
  loader: async ({ deps }) => {
    // Authenticated Discovery return must not pay Sanity RTT inside navigation/VT.
    if (await shouldSkipMarketingLanding()) {
      return { landing: DEFAULT_LANDING_PAGE };
    }
    const preview = isSanityPreviewRequest(deps.search as Record<string, unknown>);
    const landing = await getLandingPage({ data: { preview } });
    return { landing: landing ?? DEFAULT_LANDING_PAGE };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.landing.seo.title ?? "Pulse — Feel your city's pulse" },
      {
        name: "description",
        content:
          loaderData?.landing.seo.description ??
          "Curated nightlife for your city. Join the waitlist.",
      },
    ],
  }),
  component: IndexRoute,
});
